import { JsonObject, JsonProperty } from 'json2typescript';

import { Agent } from './agent';
import { AddressClientType, AdvisoryModel, ClientType, ContractInterface, ContractSignatureType, ContractStatus } from '../../../core';
import { AddressClientTypeAdapter } from '../../client/helpers/serializer/adapters/address-client-type.adapter';
import { AdvisoryModelAdapter } from '../../client/helpers/serializer/adapters/advisory-model.adapter';
import { Client } from './client';
import { ContractSignatureTypeAdapter } from '../../client/helpers/serializer/adapters/contract-signature-type.adapter';
import { ContractStatusAdapter } from '../../client/helpers/serializer/adapters/contract-status.adapter';
import { Document } from './document';
import { Id } from './id';
import { IdAdapter } from '../../client/helpers/serializer/adapters/id.adapter';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';
import { Transfer } from './transfer';
import { Validation } from './validation';

@JsonObject('Contract')
export class Contract implements ContractInterface {
    @JsonProperty('ContractCode', IdAdapter, true)
    private _code: Id = undefined;

    @JsonProperty('Code', IdAdapter, true)
    private _encryptedCode: Id = undefined;
    private _oldEncryptedCode: Id = undefined;

    @JsonProperty('Holders', [Client], true)
    private _holders: Client[] = undefined;

    @JsonProperty('Authoriseds', [Client], true)
    private _authorized: Client[] = undefined;

    @JsonProperty('SignatureType', ContractSignatureTypeAdapter, true)
    private _signatureType: ContractSignatureType = undefined;

    @JsonProperty('AppropriatenessClientId', IdAdapter, true)
    private _appropriatenessClientId: Id = undefined;

    @JsonProperty('AppropriatenessClientDocID', StringAdapter, true)
    private _appropriatenessClientDocId: string = undefined;

    @JsonProperty('AdvisoryModel', AdvisoryModelAdapter, true)
    private _advisoryModel: AdvisoryModel = undefined;

    @JsonProperty('OnboardingStatus', ContractStatusAdapter, true)
    private _status: ContractStatus = undefined;

    @JsonProperty('Agent', Agent, true)
    private _agent: Agent = undefined;

    @JsonProperty('SignatureCondition', StringAdapter, true)
    private _signatureCondition: string = undefined;

    @JsonProperty('Iban', StringAdapter, true)
    private _iban: string = undefined;

    @JsonProperty('CashAccountIBAN', StringAdapter, true)
    private _cashAccountIBAN: string = undefined;

    @JsonProperty('BankCode', StringAdapter, true)
    private _bankCode: string = undefined;

    @JsonProperty('BranchCode', StringAdapter, true)
    private _branchCode: string = undefined;

    @JsonProperty('CashAccountCode', StringAdapter, true)
    private _cashAccountCode: string = undefined;

    @JsonProperty('AddressClientId', StringAdapter, true)
    private _addressClientId: string = undefined;

    @JsonProperty('AddressClientType', AddressClientTypeAdapter, true)
    private _addressClientType: AddressClientType = AddressClientType.RESIDENCE;

    @JsonProperty('InitialTransfers', Transfer, true)
    private _transfer: Transfer = undefined;

    @JsonProperty('FeedAmount', Number, true)
    private _feedAmount: number = undefined;

    @JsonProperty('AllowTransfers', Boolean, true)
    private _allowTransfers: boolean = undefined;

    private _documents: Document[] = undefined;
    private _validation: Validation = undefined;

    constructor() {
        this._signatureType = ContractSignatureType.SOLE;
        this._advisoryModel = AdvisoryModel.C;

        const firstHolder = new Client();
        firstHolder.docExpirationDatePermanent = false;
        firstHolder.phoneExonerate = false;
        firstHolder.emailExonerate = false;

        this.addHolder(firstHolder);
    }

    get code(): Id {
        return this._code;
    }

    get encryptedCode(): Id {
        return this._encryptedCode;
    }

    get oldEncryptedCode(): Id {
        return this._oldEncryptedCode;
    }

    get holders(): Client[] {
        if (!this._holders) {
            this._holders = [];
        }

        return this._holders;
    }

    addHolder(value: Client) {
        if (!this.holders.find((it) => String(it.id) === String(value.id))) {
            this.holders.push(value);
        }
    }

    removeHolder(value: Client) {
        this.holders.map((it, idx) => {
            if (String(it.id) === String(value.id)) {
                this.holders.splice(idx, 1);
            }
        });
    }

    get authorized(): Client[] {
        if (!this._authorized) {
            this._authorized = [];
        }

        return this._authorized;
    }

    addAuthorized(value: Client) {
        if (!this.authorized.find((it) => String(it.id) === String(value.id))) {
            this.authorized.push(value);
        }
    }

    removeAuthorized(value: Client) {
        this.authorized.map((it, idx) => {
            if (String(it.id) === String(value.id)) {
                this.authorized.splice(idx, 1);
            }
        });
    }

    get legalGuardians(): Client[] {
        const legalGuardians: Client[] = [];

        this.holders.map((holder) => {
            if (holder.type !== ClientType.NATURAL_PERSON) {
                holder.legalGuardians.map((legalGuardian) => {
                    if (!legalGuardians.find((tutor) => String(tutor.id) === String(legalGuardian.id))) {
                        legalGuardians.push(legalGuardian);
                    }
                });
            }
        });
        return legalGuardians;
    }

    get signatureType(): ContractSignatureType {
        return this._signatureType;
    }

    set signatureType(value: ContractSignatureType) {
        this._signatureType = value;
    }

    get appropriatenessClientId(): Id {
        return this._appropriatenessClientId;
    }

    get appropriatenessClientDocId(): string {
        return this._appropriatenessClientDocId;
    }

    set appropriatenessClientDocId(value: string) {
        this._appropriatenessClientDocId = value;
    }

    get advisoryModel(): AdvisoryModel {
        return this._advisoryModel;
    }

    get status(): ContractStatus {
        return this._status;
    }

    isRejected(): boolean {
        return this.status === ContractStatus.REJECTED;
    }

    get agent(): Agent {
        return this._agent;
    }

    set agent(value: Agent) {
        this._agent = value;
    }

    get signatureCondition(): string {
        return this._signatureCondition;
    }

    set signatureCondition(value: string) {
        this._signatureCondition = value;
    }

    get iban(): string {
        return this._iban;
    }

    set iban(value: string) {
        this._iban = value.replace(/\s+/, '');
    }

    get cashAccountIBAN(): string {
        return this._cashAccountIBAN;
    }

    set cashAccountIBAN(value: string) {
        this._cashAccountIBAN = value.replace(/\s+/, '');
    }
    get bankCode(): string {
        return this._bankCode;
    }

    get branchCode(): string {
        return this._branchCode;
    }

    set branchCode(value: string) {
        this._branchCode = value;
    }

    get cashAccountCode(): string {
        return this._cashAccountCode;
    }

    get addressClientId(): string {
        return this._addressClientId;
    }

    set addressClientId(value: string) {
        this._addressClientId = value;
        this._addressClientType = AddressClientType.RESIDENCE;
    }

    get addressClientType(): AddressClientType {
        return this._addressClientType;
    }

    get documents(): Document[] {
        if (!this._documents) {
            this._documents = [];
        }

        return this._documents;
    }

    set documents(value: Document[]) {
        this._documents = value;
    }

    get validation(): Validation {
        if (!this._validation) {
            this._validation = new Validation(this.code);
        }

        return this._validation;
    }

    set validation(value: Validation) {
        this._validation = value;
    }

    get transfer(): Transfer {
        return this._transfer;
    }

    get feedAmount(): number {
        return this._feedAmount * 100;
    }

    set feedAmount(value: number) {
        this._feedAmount = value;
    }

    get allowTransfers(): boolean {
        return this._allowTransfers;
    }

    set allowTransfers(value: boolean) {
        this._allowTransfers = value;
    }

    isComplete(): boolean {
        return !!this._iban && !this.documents.some((document) => !document.isComplete());
    }

    isValidated(): boolean {
        return this.status === ContractStatus.FOR_SIGN;
    }

    convertToAdvisoryModel(model: AdvisoryModel = AdvisoryModel.A) {
        this._oldEncryptedCode = this._encryptedCode;
        this._code = undefined;
        this._encryptedCode = undefined;
        this._advisoryModel = model;
    }

    isAdvisoryModelSuitabilityOrGDC(): boolean {
        return (
            this._advisoryModel === AdvisoryModel.A || this._advisoryModel === AdvisoryModel.P || this._advisoryModel === AdvisoryModel.G
        );
    }

    isAdvisoryModelSuitability(): boolean {
        return this._advisoryModel === AdvisoryModel.A || this._advisoryModel === AdvisoryModel.P;
    }

    isAdvisoryModelGDC(): boolean {
        return this._advisoryModel === AdvisoryModel.G;
    }
}
